import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {CheckIcon, PersonIcon, StopIcon} from '@primer/octicons-react'
import {Button, FormControl, Heading} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'

import {type ActorType, Role} from '../../../../api/common-contracts'
import type {SuggestedCollaborator} from '../../../../api/memex/contracts'
import {BorderlessTextInput} from '../../../../components/common/borderless-text-input'
import SuggestionsList from '../../../../components/suggestions-list'
import {ToastType} from '../../../../components/toasts/types'
import useToasts from '../../../../components/toasts/use-toasts'
import {isCopilot} from '../../../../helpers/assignee-helper'
import {getInitialState} from '../../../../helpers/initial-state'
import {useTimeout} from '../../../../hooks/common/timeouts/use-timeout'
import {CommitState} from '../../../../hooks/use-autosave'
import {useClickOutside} from '../../../../hooks/use-click-outside'
import {useValueDelayedByTimeout} from '../../../../hooks/use-value-delayed-by-timeout'
import {useUpdateCollaboratorsOptimistically} from '../../../../queries/collaborators'
import {actorIdentifier, getCollaboratorDisplayValue} from '../../../../queries/organization-access'
import {useSuggestedCollaborators} from '../../../../queries/suggested-collaborators'
import {ManageAccessResources} from '../../../../strings'
import styles from './add-collaborators.module.css'
import {CollaboratorPill} from './collaborator-pill'
import {CollaboratorRoleDropDown} from './collaborator-role-drop-down'

/** Component for displaying success after a collaborator has been added */
const AddCollaboratorSuccess = ({
  collaboratorsAddedCount,
  setInvitationRequestState,
}: {
  setInvitationRequestState: React.Dispatch<React.SetStateAction<CommitState>>
  collaboratorsAddedCount: string
}) => {
  const dismissAfterDelay = useTimeout(() => setInvitationRequestState(CommitState.None), 5000)

  useEffect(() => {
    dismissAfterDelay()
  }, [dismissAfterDelay])

  return (
    <div {...testIdProps('success-message')}>
      <Octicon icon={CheckIcon} className={styles.Octicon} />
      <span> {collaboratorsAddedCount} successfully added </span>
    </div>
  )
}

/** Component for displaying failure if a collaborator cannot be added */
const AddCollaboratorError = () => {
  return (
    <div {...testIdProps('failure-message')}>
      <Octicon icon={StopIcon} className={styles.Octicon_1} />
      <span> Unable to add some users </span>
    </div>
  )
}

const FILTER_COLLABORATORS_LIST_ID = 'add-collaborators-suggestions-list'

const defaultEmptyArray: Array<any> = []

const defaultSuggestedCollaborators: Array<SuggestedCollaborator> = []

export const AddCollaborators: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const {projectOwner} = getInitialState()
  const [isInputActive, setIsInputActive] = useState(false)
  const [showCollaboratorSuggestionsList, setShowCollaboratorSuggestionsList] = useState<boolean>(false)
  const [selectedRole, setSelectedRole] = useState<Role>(Role.Write)
  const [selectedCollaborators, setSelectedCollaborators] = useState<Array<SuggestedCollaborator>>(defaultEmptyArray)

  const [invitationRequestState, setInvitationRequestState] = useState<CommitState>(CommitState.None)
  const [collaboratorsAddedCount, setCollaboratorsAddedCount] = useState('')
  const [value, setValue] = useState('')
  const deferredValue = useValueDelayedByTimeout(value.startsWith('@') ? value.trim().slice(1) : value.trim())

  const {data: suggestedCollaborators = defaultSuggestedCollaborators} = useSuggestedCollaborators({
    variables: {query: deferredValue},
  })

  useClickOutside([listRef, inputRef], () => setShowCollaboratorSuggestionsList(false))

  const removeCollaboratorById = useCallback((collab: {id: number; actor_type: ActorType}) => {
    setSelectedCollaborators(previousCollaborators => {
      const newCollaborators = previousCollaborators.filter(
        collaborator => actorIdentifier(collaborator) !== actorIdentifier(collab),
      )
      if (newCollaborators.length === previousCollaborators.length) {
        return previousCollaborators
      }
      return newCollaborators
    })
  }, [])

  const updateInvitationsList = useCallback(
    (failed: Array<string>) => {
      const failedMap = new Set(failed)
      setSelectedCollaborators(previousSelectedCollaborators => {
        const failedCollaborators = new Array<SuggestedCollaborator>()
        for (const collab of previousSelectedCollaborators) {
          if (failedMap.has(`${actorIdentifier(collab)}`)) {
            failedCollaborators.push(collab)
          }
        }
        return failedCollaborators
      })
    },
    [setSelectedCollaborators],
  )

  const selectCollaboratorById = (identifier: string) => {
    const collaboratorToSelect = suggestedCollaborators.find(
      suggestedCollaborator => actorIdentifier(suggestedCollaborator) === identifier,
    )
    if (!collaboratorToSelect) return
    setSelectedCollaborators(previousSelectedCollaborators => {
      const collaboratorAlreadySelected = previousSelectedCollaborators.some(
        selectedCollaborator => actorIdentifier(selectedCollaborator) === actorIdentifier(collaboratorToSelect),
      )
      if (collaboratorAlreadySelected) {
        return previousSelectedCollaborators
      }
      return [...previousSelectedCollaborators, collaboratorToSelect]
    })
  }

  const onSuggestionSelect = (suggestion: SuggestedCollaborator) => {
    if (suggestion.isCollaborator) {
      return
    }

    setShowCollaboratorSuggestionsList(false)
    setValue('')
    selectCollaboratorById(actorIdentifier(suggestion))

    inputRef.current?.focus()
  }

  const onChange = useCallback(({target}: React.ChangeEvent<HTMLInputElement>) => {
    setValue(target.value)
  }, [])

  const onFocus = useCallback(() => {
    setShowCollaboratorSuggestionsList(true)
    setIsInputActive(true)
  }, [setShowCollaboratorSuggestionsList, setIsInputActive])

  const onBlur = useCallback(() => {
    setIsInputActive(false)
  }, [setIsInputActive])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (event.key) {
        case 'Backspace': {
          if (value === '') {
            if (selectedCollaborators.length > 0) {
              const lastCollab = selectedCollaborators[selectedCollaborators.length - 1]
              if (lastCollab) {
                removeCollaboratorById(lastCollab)
              }
            }
          }
          break
        }
      }

      if (invitationRequestState !== CommitState.None) {
        setInvitationRequestState(CommitState.None)
        setCollaboratorsAddedCount('')
      }
    },
    [invitationRequestState, value, selectedCollaborators, removeCollaboratorById],
  )
  const {addToast} = useToasts()

  const {mutate: updateCollaboratorsMutate} = useUpdateCollaboratorsOptimistically()
  const onSubmit = useCallback(() => {
    if (selectedCollaborators.length === 0) {
      return
    }

    return updateCollaboratorsMutate(
      {
        role: selectedRole,
        collaborators: selectedCollaborators.map(i => actorIdentifier(i)),
      },
      {
        onSuccess: ({failed}) => {
          if (failed.length > 0) {
            setInvitationRequestState(CommitState.Failed)
            updateInvitationsList(failed)
          } else {
            setValue('')
            const count = selectedCollaborators.length
            setCollaboratorsAddedCount(`${count} ${count === 1 ? 'user' : 'users'}`)
            setInvitationRequestState(CommitState.Successful)
            setSelectedCollaborators(defaultEmptyArray)
          }
        },
        onError: error => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: ToastType.error,
            message:
              error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
                ? error.message
                : 'Failed to update collaborators',
          })
        },
      },
    )
  }, [addToast, selectedCollaborators, selectedRole, updateCollaboratorsMutate, updateInvitationsList])

  useLayoutEffect(() => {
    const normalizedValue = deferredValue.startsWith('@')
      ? deferredValue.substring(1, deferredValue.length)
      : deferredValue
    const hasNormalizedValue = normalizedValue.length > 0

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowCollaboratorSuggestionsList(hasNormalizedValue)
  }, [deferredValue])

  const onPillRemoved = useCallback(
    (collab: {id: number; actor_type: ActorType}) => {
      removeCollaboratorById(collab)
      if (invitationRequestState !== CommitState.None) {
        setInvitationRequestState(CommitState.None)
      }
    },
    [setInvitationRequestState, invitationRequestState, removeCollaboratorById],
  )

  const addCollaboratorsLabel =
    projectOwner && projectOwner.type === 'organization'
      ? ManageAccessResources.addCollaboratorPlaceholderOrgProject
      : ManageAccessResources.addCollaboratorPlaceholderUserProject

  const isSuggestedCollaboratorsListVisible =
    suggestedCollaborators.length > 0 && deferredValue.length > 0 && showCollaboratorSuggestionsList

  return (
    <div className={styles.Box} {...testIdProps('add-collaborators')}>
      <FormControl className={styles.FormControl}>
        <FormControl.Label visuallyHidden>Add Collaborators</FormControl.Label>
        <div className={styles.Box_1}>
          <Heading as="h3" className={styles.Heading}>
            Invite collaborators
          </Heading>
          <div>
            <div aria-live="polite">
              {invitationRequestState === CommitState.Successful && (
                <AddCollaboratorSuccess
                  setInvitationRequestState={setInvitationRequestState}
                  collaboratorsAddedCount={collaboratorsAddedCount}
                />
              )}
              {invitationRequestState === CommitState.Failed && <AddCollaboratorError />}
            </div>
          </div>
        </div>
        <div className={styles.Box_2}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            ref={containerRef}
            onClick={() => inputRef.current?.focus()}
            className={clsx(styles.Container, isInputActive && styles.isInputActive)}
          >
            <Octicon icon={PersonIcon} className={styles.Octicon_2} />
            <div className={styles.Box_4}>
              {selectedCollaborators.map(collaborator => {
                const id = actorIdentifier(collaborator)
                return (
                  <CollaboratorPill
                    key={id}
                    avatarUrl={collaborator.avatarUrl}
                    login={getCollaboratorDisplayValue(collaborator)}
                    id={id}
                    onRemove={() => onPillRemoved(collaborator)}
                  />
                )
              })}
              <BorderlessTextInput
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isSuggestedCollaboratorsListVisible}
                aria-autocomplete="list"
                aria-controls={FILTER_COLLABORATORS_LIST_ID}
                aria-label={addCollaboratorsLabel}
                className={styles.BorderlessTextInput}
                autoComplete="off"
                value={value}
                ref={inputRef}
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                placeholder={addCollaboratorsLabel}
                id="add-collaborators-input"
                {...testIdProps('add-collaborators-input')}
              />

              {isSuggestedCollaboratorsListVisible && (
                <SuggestionsList
                  testId={FILTER_COLLABORATORS_LIST_ID}
                  listRef={listRef}
                  controllingElementRef={inputRef}
                  xOriginEdgeAlign="left"
                  style={{width: '400px'}}
                  items={suggestedCollaborators.map(collaborator => {
                    return {
                      renderItem: () => <CollabSuggestionItem suggestion={collaborator} />,
                      key: collaborator.id,
                      value: getCollaboratorDisplayValue(collaborator),
                      asHTML: true,
                      onSelect: () => onSuggestionSelect(collaborator),
                      testId: `collaborator-suggestion-item-${getCollaboratorDisplayValue(collaborator)}`,
                    }
                  })}
                />
              )}
            </div>
          </div>
          <div className={styles.Box_5}>
            <CollaboratorRoleDropDown selectedRoles={[selectedRole]} handleOnClick={setSelectedRole} />
          </div>
          <Button variant="primary" onClick={onSubmit} {...testIdProps('add-collaborators-invite-button')}>
            Invite
          </Button>
        </div>
      </FormControl>
    </div>
  )
}

function CollabSuggestionItem({suggestion}: {suggestion: SuggestedCollaborator}) {
  const displayValue = getCollaboratorDisplayValue(suggestion)
  return (
    <span>
      {isCopilot(suggestion.name) ? (
        <CopilotAvatar className={styles.GitHubAvatar} />
      ) : (
        <GitHubAvatar loading="lazy" alt={displayValue} src={suggestion.avatarUrl} className={styles.GitHubAvatar} />
      )}
      <span className={clsx(styles.suggestionItem, suggestion.isCollaborator && styles.collabSuggestionItem)}>
        {suggestion.name} <span className={styles.Text}>{displayValue}</span>
      </span>
      {suggestion.isCollaborator && <span className={styles.Text_1}>&#183; Already a collaborator</span>}
    </span>
  )
}
